// src/pages/funds.ts —— 06 C-08 充值 / 提现审核（GET /api/admin/reviews 四页签 + 多付结案 GET /api/admin/withdrawal-overpayments）与 C-03 人工调账表单（POST /api/admin/users/{id}/adjust）。
// D9：提币通过后进入待支付（approved），回填哈希推进 paying → worker 链上验证置 paid / failed；不再本地生成代付单与回调（原型 setReview 废弃，附录 A-7）。
// adjustForm 签名受契约锁约束（TD-851）
import { ApiError } from '../../../shared/src/api/client';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { Dict } from '../../../shared/src/dc';
import { fmtAmount, parseDecimalInput } from '../../../shared/src/money';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openConfirm, openDrawer, openForm, reloadCurrent, sessionGuard } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_FUNDS, REWRITES_FUNDS } from '../i18n/funds';
import { wdApprovedToast } from './funds.toast';
import type { AdjustResult, OverpayRow, ReviewResult, ReviewRow } from './funds.types';
import { openUser360 } from './shells';
import { chipMap, defineView, type ActionDef, type DrawerAction, type ErasedView, type FieldDef, type FormDef, type Need, type PageMap, type RowGate } from './types';
import { cell, chip, chipOf, localErr, type CellVals, type ChipKind } from './ui';

const R_APPROVE: Need = { module: 'review', bit: 'approve' };
const NOTE_MAX = 255;

const tz = (ctx: AdminCtx): string => ctx.s.sh.timezone;

// D8-12 截断两位展示；后端值非法时原样显示（不抛错）
function amt(v: string): string {
  try {
    return fmtAmount(v, 2);
  } catch {
    return v;
  }
}

// 06 C-08：地址 / 哈希列 `前4…后4`（抽屉 wide 字段给全文）；EVM 地址前缀 0x 不计入前 4 位（原型 L553 `0x8f3C…A063`）
function short(v: string | null): string {
  if (!v) return '—';
  return v.length > 12 ? `${v.slice(0, 4)}…${v.slice(-4)}` : v;
}
function shortAddr(v: string): string {
  return /^0x[0-9a-fA-F]{40}$/.test(v) ? `${v.slice(0, 6)}…${v.slice(-4)}` : short(v);
}

// 06 C-08 状态集：充币 5 态 + 提币 6 态（D9 / S-65 / X-34）
const REVIEW_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = {
  detected: ['gray', 'stDetected'], confirming: ['blue', 'stConfirming'], credited: ['green', 'stPass'], manual_review: ['amber', 'stPending'], // 06 C-08：已通过 / 审核中 = 原型 stPass / stPending
  pending_review: ['amber', 'stPending'], approved: ['blue', 'stApproved'], paying: ['blue', 'stPaying'], paid: ['green', 'stPaidWd'],
  rejected: ['red', 'stReject'], failed: ['red', 'stVerifyFailed'],
};
const OVERPAY_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = { pending: ['amber', 'stPending'], approved: ['green', 'stPass'], rejected: ['red', 'stReject'] };
const RECOVERY_STATUS: Readonly<Record<string, readonly [ChipKind, string]>> = { open: ['amber', 'recOpen'], recovered: ['green', 'recRecovered'], written_off: ['gray', 'recWrittenOff'] };

// facet 选项文案（P-12）：按列章映射翻译，未知值显示原值；status 另含虚拟值 pending（同 tab=pending）
function labelFrom(map: Readonly<Record<string, readonly [ChipKind, string]>>): (t: Dict, v: string) => string {
  return (t, v) => {
    const hit = map[v];
    return (hit && str(t, hit[1])) || v;
  };
}
const statusLabel = (t: Dict, v: string): string => (v === 'pending' ? str(t, 'stPending') || v : labelFrom(REVIEW_STATUS)(t, v));
const kindLabel = (t: Dict, v: string): string => (v === 'deposit' ? strArr(t, 'tabReview')[1] : v === 'withdrawal' ? strArr(t, 'tabReview')[2] : undefined) ?? v;
const RESULT_OPTIONS = ['recovered', 'written_off'] as const;
const resultLabel = labelFrom(RECOVERY_STATUS);

// D9 / S-04：TRC20 64 hex；ERC20 / BEP20 0x + 64 hex
export const TX_HASH_RE: Readonly<Record<string, RegExp>> = { TRC20: /^[0-9a-fA-F]{64}$/, ERC20: /^0x[0-9a-fA-F]{64}$/, BEP20: /^0x[0-9a-fA-F]{64}$/ };

const isPendingRow = (r: ReviewRow): boolean => (r.kind === 'deposit' ? r.status === 'manual_review' : r.status === 'pending_review');
// S-04：一审人 = 当前管理员的待二审行，通过前即拦截（与通用 1501 区分）
const sameReviewer = (ctx: AdminCtx, r: ReviewRow): boolean => r.reviewed_by !== null && r.reviewed_by.account === ctx.s.sh.me?.account;
const isWd = (r: ReviewRow): boolean => r.kind === 'withdrawal';
// O-21：需二审（含大额一审前）或带地址风险标记的待审提币只能在抽屉内通过
const needsDrawer = (r: ReviewRow): boolean => isWd(r) && (r.needs_second_review || (r.address_flags?.length ?? 0) > 0);
const platformHit = (r: ReviewRow): boolean => isWd(r) && (r.address_flags ?? []).includes('platform_address');
// 06 C-08 [本册定]：reviewed_by = 当前管理员的待二审行「通过」预置灰，title = errSameReviewer（点击仍由 decide 拦截兜底）
const sameReviewerGate = (ctx: AdminCtx, r: ReviewRow): RowGate => (sameReviewer(ctx, r) ? { disabled: true, title: str(ctx.t, 'errSameReviewer') } : { disabled: false, title: '' });

type ReviewRoute =
  | 'POST /api/admin/reviews/deposit/{biz_no}/approve'
  | 'POST /api/admin/reviews/deposit/{biz_no}/reject'
  | 'POST /api/admin/reviews/withdrawal/{biz_no}/approve'
  | 'POST /api/admin/reviews/withdrawal/{biz_no}/reject';

function reviewRoute(r: ReviewRow, act: 'approve' | 'reject'): ReviewRoute {
  if (r.kind === 'deposit') return act === 'approve' ? 'POST /api/admin/reviews/deposit/{biz_no}/approve' : 'POST /api/admin/reviews/deposit/{biz_no}/reject';
  return act === 'approve' ? 'POST /api/admin/reviews/withdrawal/{biz_no}/approve' : 'POST /api/admin/reviews/withdrawal/{biz_no}/reject';
}

// 通过 / 拒绝（行内与抽屉共用）：note 可空（≤255，超长由后端 4220）。结果按 kind / stage 选 toast（06 C-08 toast 表）
async function decide(ctx: AdminCtx, r: ReviewRow, act: 'approve' | 'reject', note: string): Promise<void> {
  const t = ctx.t;
  if (act === 'approve' && sameReviewer(ctx, r)) {
    ctx.gp.ui.toast(str(t, 'errSameReviewer'));
    return;
  }
  if (note.length > NOTE_MAX) {
    ctx.gp.ui.toast(str(t, 'errNoteLong'));
    return;
  }
  const alive = sessionGuard(ctx);
  let res: ReviewResult;
  try {
    res = await ctx.gp.api.call<ReviewResult>(reviewRoute(r, act), { path: { biz_no: r.biz_no }, body: { note } });
  } catch (e) {
    if (!alive()) return;
    // 1501 在审核动作上即同人二审（S-04），优先于通用 toastNoPerm
    ctx.gp.ui.toast(act === 'approve' && e instanceof ApiError && e.code === 1501 ? str(t, 'errSameReviewer') : ctx.gp.ui.err(e));
    return;
  }
  if (!alive()) return;
  const key = act === 'reject' ? 'toastRejected' : r.kind === 'deposit' ? 'toastApproved' : res.stage === 'first' ? 'toastFirstApproved' : '';
  ctx.gp.ui.toast(key === '' ? wdApprovedToast(t, res) : str(t, key));
  ctx.events.emit('profile', {}); // 侧栏待审核徽标立即刷新
  closeDrawer(ctx);
  await reloadCurrent(ctx);
}

// 手工登记已付哈希 / 重新登记（人工兜底：正常付款由财务工作台完成；approved / failed → paying）：按网络本地校验；4220 tx_hash（格式 / 已被占用）→ errTxHash；1605（财务在途）由 ui.err 显示服务端 message
function markPaidForm(t: Dict, r: ReviewRow): FormDef {
  const route: RouteKey = 'POST /api/admin/reviews/withdrawal/{biz_no}/mark-paid';
  const re = TX_HASH_RE[r.network] ?? TX_HASH_RE['TRC20'];
  return {
    title: `${str(t, r.status === 'failed' ? 'aMarkPaidAgain' : 'aMarkPaid')} · ${r.biz_no}`,
    tip: str(t, 'markPaidTip').replace('{network}', r.network),
    fields: [{ k: 'tx_hash', label: str(t, 'cTxHash'), type: 'text', wide: true }],
    init: { tx_hash: '' },
    validate: (v) => (re?.test(String(v['tx_hash'] ?? '').trim()) ? '' : str(t, 'errTxHash')),
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call(route, { path: { biz_no: r.biz_no }, body: { tx_hash: String(v['tx_hash']).trim() } });
      } catch (e) {
        if (e instanceof ApiError && e.code === 4220 && e.data.field === 'tx_hash') throw localErr(route, str(t, 'errTxHash'));
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastMarkPaid'));
      closeDrawer(c);
      void reloadCurrent(c);
    },
  };
}

// 备注表单（多付结案提议 / 复核通过 / 驳回）：noteRequired = 02 §6.2（提议、驳回必填；复核通过可空）
function noteForm(t: Dict, title: string, route: RouteKey, path: Readonly<Record<string, string | number>>, toastKey: string, noteRequired: boolean): FormDef {
  return {
    title,
    fields: [{ k: 'note', label: str(t, 'reviewNote'), type: 'text', wide: true, optional: !noteRequired }],
    init: { note: '' },
    validate: (v) => {
      const n = String(v['note'] ?? '').trim();
      if (noteRequired && n.length < 1) return str(t, 'errNoteRequired');
      return n.length > NOTE_MAX ? str(t, 'errNoteLong') : '';
    },
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call(route, { path, body: { note: String(v['note'] ?? '').trim() } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, toastKey));
      closeDrawer(c);
      void reloadCurrent(c);
    },
  };
}

function proposeOverpaidForm(t: Dict, r: ReviewRow): FormDef {
  return noteForm(t, `${str(t, 'proposeOverpaid')} · ${r.biz_no}`, 'POST /api/admin/reviews/withdrawal/{biz_no}/close-overpaid', { biz_no: r.biz_no }, 'toastOverpaidProposed', true);
}

// 退回（failed → rejected）：全额。部分退回（paid_amount）06 未要求 UI，不暴露（J-13）；1605 由 doConfirm 以 ui.err toast
function refundConfirm(ctx: AdminCtx, r: ReviewRow): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'wdRefundTitle'),
    text: str(t, 'wdRefundText'),
    label: r.biz_no,
    run: async (c) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/reviews/withdrawal/{biz_no}/refund', { path: { biz_no: r.biz_no }, body: {} });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastWdRefunded'));
      closeDrawer(c);
      await reloadCurrent(c);
    },
  });
}

// 06 C-08 行内动作表
function reviewActions(t: Dict): readonly ActionDef<ReviewRow>[] {
  return [
    { label: 'aReject', need: R_APPROVE, danger: true, show: isPendingRow, run: (c, r) => decide(c, r, 'reject', '') },
    // O-21：大额 / 待二审提币不给行内「通过」，须进抽屉核对风险结论后再通过
    { label: 'aApprove', need: R_APPROVE, show: (r) => isPendingRow(r) && !needsDrawer(r), gate: sameReviewerGate, run: (c, r) => decide(c, r, 'approve', '') },
    { label: 'aMarkPaid', need: R_APPROVE, show: (r) => isWd(r) && r.status === 'approved', run: (c, r) => openForm(c, markPaidForm(t, r)) },
    { label: 'aMarkPaidAgain', need: R_APPROVE, show: (r) => isWd(r) && r.status === 'failed', run: (c, r) => openForm(c, markPaidForm(t, r)) },
    { label: 'fndRefund', need: R_APPROVE, danger: true, show: (r) => isWd(r) && r.status === 'failed', run: (c, r) => refundConfirm(c, r) },
    { label: 'aDetail', show: (r) => (isPendingRow(r) ? needsDrawer(r) : !(isWd(r) && (r.status === 'approved' || r.status === 'failed'))), run: (c, r) => openReview(c, r) },
  ];
}

// 06 C-08 金额列 sub：到账 {net} · 大额标记（未一审 bigWdTag / 已一审 waitSecondReview · 一审 {账号}）
function amountSub(t: Dict, r: ReviewRow): string {
  const parts: string[] = [];
  if (r.net_amount !== null) parts.push(`${str(t, 'netArrive')} ${amt(r.net_amount)}`);
  if (isWd(r) && r.needs_second_review && isPendingRow(r)) parts.push(r.reviewed_by ? `${str(t, 'waitSecondReview')} · ${str(t, 'cReview1')} ${r.reviewed_by.account}` : str(t, 'bigWdTag'));
  return parts.join(' · ');
}

// 06 C-08 / C-12：approved 超 SLA 满 1 小时时状态章下方红字「超时 {h}h」（D26；接口字段 sla_overdue_hours 未超时为 null）
export function slaChip(t: Dict, c: CellVals, overdueHours: number | null): CellVals {
  return overdueHours === null ? c : { ...c, sub: str(t, 'slaOverdue').replace('{h}', String(overdueHours)), subDanger: true };
}

const statusText = (t: Dict, r: ReviewRow): string => chipOf(chipMap(t, REVIEW_STATUS), r.status, 'review.status').v;

// O-21 抽屉风险字段：风控命中（规则 · 时间）、地址首次使用、地址风险标记；旧后端未下发时显示 —
const ruleLabel = (t: Dict, key: string): string => str(t, `fndRule_${key}`) || key;
const flagLabel = (t: Dict, f: string): string => str(t, `fndFlag_${f}`) || f;
function riskFields(ctx: AdminCtx, r: ReviewRow): ReadonlyArray<{ k: string; v: string; wide?: boolean }> {
  const t = ctx.t;
  const hits = r.risk_hits;
  const flags = r.address_flags;
  return [
    { k: str(t, 'fndRiskHits'), v: !hits ? '—' : hits.length === 0 ? str(t, 'fndNone') : hits.map((h) => `${ruleLabel(t, h.rule_key)} · ${fmtDateTime(h.created_at, { tz: tz(ctx) })}`).join('；'), wide: true },
    { k: str(t, 'fndAddrFirstSeen'), v: r.address_first_seen === undefined || r.address_first_seen === null ? '—' : str(t, r.address_first_seen ? 'fndFirstYes' : 'fndFirstNo') },
    { k: str(t, 'fndAddrFlags'), v: !flags ? '—' : flags.length === 0 ? str(t, 'fndNone') : flags.map((f) => flagLabel(t, f)).join('；'), wide: true },
  ];
}

// 06 C-08 抽屉：底部按钮按状态分流（D9）——pending 拒绝 / 通过；approved 回填哈希；failed 重新回填 / 退回（+ 多付结案提议）；其余终态 用户 360
export function openReview(ctx: AdminCtx, r: ReviewRow): void {
  const t = ctx.t;
  const pending = isPendingRow(r);
  const note = (c: AdminCtx): string => c.host.state.tb.note.trim();
  const userLabel = r.user.email ?? r.user.uid;
  let actions: DrawerAction[];
  if (pending) {
    actions = [
      { label: str(t, 'aReject'), kind: 'danger', need: R_APPROVE, run: (c) => decide(c, r, 'reject', note(c)) },
      { label: str(t, 'aApprove'), kind: 'green', need: R_APPROVE, gate: (c) => (platformHit(r) ? { disabled: true, title: str(c.t, 'fndPlatformBlock') } : sameReviewerGate(c, r)), run: (c) => (platformHit(r) ? c.gp.ui.toast(str(c.t, 'fndPlatformBlock')) : decide(c, r, 'approve', note(c))) },
    ];
  } else if (isWd(r) && r.status === 'approved') {
    actions = [{ label: str(t, 'aMarkPaid'), kind: 'green', need: R_APPROVE, run: (c) => openForm(c, markPaidForm(t, r)) }];
  } else if (isWd(r) && r.status === 'failed') {
    actions = [
      ...(r.tx_hash ? [{ label: str(t, 'proposeOverpaid'), kind: '' as const, need: R_APPROVE, run: (c: AdminCtx) => openForm(c, proposeOverpaidForm(t, r)) }] : []),
      { label: str(t, 'fndRefund'), kind: 'danger', need: R_APPROVE, run: (c) => refundConfirm(c, r) },
      { label: str(t, 'aMarkPaidAgain'), kind: 'green', need: R_APPROVE, run: (c) => openForm(c, markPaidForm(t, r)) },
    ];
  } else {
    actions = [{ label: str(t, 'aOpen360'), kind: 'primary', run: (c) => openUser360(c, r.user.id) }];
  }
  openDrawer(ctx, {
    kind: str(t, 'review'),
    title: `${r.biz_no} · ${amt(r.amount)} USDT`,
    subtitle: r.status === 'failed' && isWd(r) ? `${userLabel} · ${str(t, 'wdFailedTip')}` : pending && platformHit(r) ? `${userLabel} · ${str(t, 'fndPlatformBlock')}` : userLabel,
    fields: [
      { k: str(t, 'cUser'), v: userLabel },
      { k: str(t, 'cKind'), v: kindLabel(t, r.kind) },
      { k: str(t, 'cNetwork'), v: r.network },
      { k: str(t, 'cStatus'), v: statusText(t, r) },
      ...(!isWd(r) && r.from_address ? [{ k: str(t, 'fndFromAddr'), v: r.from_address, wide: true }] : []),
      { k: str(t, isWd(r) ? 'fndToAddrWd' : 'fndToAddrDep'), v: r.to_address, wide: true },
      { k: str(t, 'cTxHash'), v: r.tx_hash ?? '—', wide: true },
      { k: str(t, 'cApplied'), v: fmtDateTime(r.created_at, { tz: tz(ctx) }) },
      ...(isWd(r)
        ? [
            { k: str(t, 'cReview1'), v: r.reviewed_by?.account ?? '—' },
            { k: str(t, 'cReview2'), v: r.second_reviewed_by ? `${r.second_reviewed_by.account} · ${fmtDateTime(r.second_reviewed_at, { tz: tz(ctx) })}` : '—' },
            { k: str(t, 'fndApprovedAt'), v: r.approved_at ? fmtDateTime(r.approved_at, { tz: tz(ctx) }) : '—' },
            { k: `${str(t, 'cFee')} · ${str(t, 'netArrive')}`, v: `${r.fee === null ? '—' : amt(r.fee)} / ${r.net_amount === null ? '—' : amt(r.net_amount)}` },
            ...riskFields(ctx, r),
          ]
        : [
            { k: str(t, 'cConfirmations'), v: r.confirmations === null ? '—' : `${r.confirmations} / ${r.required_confirmations ?? '—'}` },
            { k: str(t, 'depActualUSDT'), v: `${r.amount} USDT` },
            // 用户拍板 2026-10-15：钱包侧金额（平台账额 / 入账总额 / 手续费 / 净到账 / 汇率差）一律写 USDT
            ...(r.fx_base_rate && r.fx_applied_rate ? [
              { k: str(t, 'depBaseRate'), v: r.fx_base_rate },
              { k: str(t, 'depAppliedRate'), v: r.fx_applied_rate },
              { k: str(t, 'depPlatformAmount'), v: r.platform_amount ? `${r.platform_amount} USDT` : '—' },
              { k: str(t, 'depGross'), v: r.credited_gross ? `${r.credited_gross} USDT` : '—' },
              { k: str(t, 'depFeeUSD'), v: r.fee ? `${r.fee} USDT` : '—' },
              { k: str(t, 'depNetUSD'), v: r.net_amount ? `${r.net_amount} USDT` : '—' },
              { k: str(t, 'depSpread'), v: r.fx_spread ? `${r.fx_spread} USDT` : '—' },
            ] : []),
          ]),
      ...(r.review_note ? [{ k: str(t, 'reviewNote'), v: r.review_note, wide: true }] : []),
    ],
    note: pending,
    actions,
  });
}

function reviewView(key: 'pending' | 'deposit' | 'withdrawal' | 'done'): ErasedView {
  return defineView<ReviewRow>({
    key,
    source: { route: 'GET /api/admin/reviews', tabParam: 'tab' },
    echo: '/admin/reviews',
    cols: [
      { label: 'cKind', facet: 'kind', facetLabel: kindLabel }, { label: 'cUser' }, { label: 'cAmount' }, { label: 'cNetwork' }, { label: 'cTxHash' },
      { label: 'cApplied', sortKey: 'created_at' }, { label: 'cStatus', facet: 'status', facetLabel: statusLabel },
    ],
    cells: (c, r): readonly CellVals[] => {
      const tabs = strArr(c.t, 'tabReview');
      return [
        r.kind === 'deposit' ? chip('green', `↓ ${tabs[1] ?? ''}`) : chip('purple', `↑ ${tabs[2] ?? ''}`),
        cell(r.user.email ?? r.user.uid, r.biz_no),
        cell(`${amt(r.amount)} USDT`, amountSub(c.t, r)),
        cell(r.network, shortAddr(r.to_address), true),
        cell(short(r.tx_hash), '', true),
        cell(fmtDateTime(r.created_at, { tz: tz(c) })),
        slaChip(c.t, chipOf(chipMap(c.t, REVIEW_STATUS), r.status, 'review.status'), r.sla_overdue_hours),
      ];
    },
    open: openReview,
    rowActions: (c) => reviewActions(c.t),
    csvName: 'review',
  });
}

// 02 §6.2：结清 {result: recovered | written_off, note 必填}；执行人 ≠ 发起人（1501 由 ui.err 显示 toastNoPerm）
function settleOverpayForm(t: Dict, o: OverpayRow): FormDef {
  return {
    title: `${str(t, 'aSettleOverpay')} · ${o.biz_no}`,
    fields: [
      { k: 'result', label: str(t, 'cRecovery'), type: 'select', options: RESULT_OPTIONS, optionLabel: resultLabel },
      { k: 'note', label: str(t, 'reviewNote'), type: 'text', wide: true },
    ],
    init: { result: 'recovered', note: '' },
    validate: (v) => {
      const n = String(v['note'] ?? '').trim();
      return n.length < 1 ? str(t, 'errNoteRequired') : n.length > NOTE_MAX ? str(t, 'errNoteLong') : '';
    },
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/withdrawal-overpayments/{id}/settle', { path: { id: o.id }, body: { result: String(v['result']), note: String(v['note']).trim() } });
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastOverpaidSettled'));
      void reloadCurrent(c);
    },
  };
}

// 多付结案（02 §6.2）：无 tab / sort；facet status / recovery_status；关键词与日期不下发（传入 → 4220）
const overpaidView = defineView<OverpayRow>({
  key: 'overpaid',
  source: { route: 'GET /api/admin/withdrawal-overpayments', tabParam: 'none' },
  echo: '/admin/withdrawal-overpayments',
  noDate: true,
  noKeyword: true,
  cols: [
    { label: 'cBizNo' }, { label: 'cNetwork' }, { label: 'cNetAmount' }, { label: 'cChainAmount' }, { label: 'cOverpaid' },
    { label: 'cStatus', facet: 'status', facetLabel: labelFrom(OVERPAY_STATUS) }, { label: 'cRecovery', facet: 'recovery_status', facetLabel: resultLabel }, { label: 'cTime' },
  ],
  cells: (c, o) => [
    cell(o.biz_no, short(o.tx_hash), true),
    cell(o.network),
    cell(amt(o.net_amount)),
    cell(amt(o.chain_amount)),
    cell(amt(o.overpaid_amount), o.proposed_by ? `${str(c.t, 'proposedBy')} ${o.proposed_by.account}` : ''),
    chipOf(chipMap(c.t, OVERPAY_STATUS), o.status, 'overpay.status'),
    chipOf(chipMap(c.t, RECOVERY_STATUS), o.recovery_status, 'overpay.recovery'),
    cell(fmtDateTime(o.created_at, { tz: tz(c) })),
  ],
  rowActions: (c) => [
    {
      label: 'aReject', need: R_APPROVE, danger: true, show: (o) => o.status === 'pending',
      run: (cc, o) => openForm(cc, noteForm(c.t, `${str(c.t, 'aReject')} · ${o.biz_no}`, 'POST /api/admin/reviews/withdrawal/{biz_no}/close-overpaid/reject', { biz_no: o.biz_no }, 'toastRejected', true)),
    },
    {
      label: 'aApprove', need: R_APPROVE, show: (o) => o.status === 'pending',
      run: (cc, o) => openForm(cc, noteForm(c.t, `${str(c.t, 'aApprove')} · ${o.biz_no}`, 'POST /api/admin/reviews/withdrawal/{biz_no}/close-overpaid/approve', { biz_no: o.biz_no }, 'toastApproved', false)),
    },
    { label: 'aSettleOverpay', need: R_APPROVE, show: (o) => o.status === 'approved' && o.recovery_status === 'open', run: (cc, o) => openForm(cc, settleOverpayForm(c.t, o)) },
  ],
  csvName: 'withdrawal-overpayments',
});

// 06 C-03 金额 (USDT, +/-)：有符号非零、≤6 位小数（负数绝对值 ≤ 可用余额由服务端 1101 校验）
function adjustAmount(raw: string): string | null {
  const m = /^(-?)(\d+(?:\.\d{1,6})?)$/.exec(raw.trim());
  const abs = m?.[2] ? parseDecimalInput(m[2], 6) : null;
  if (abs === null || /^0+(\.0+)?$/.test(abs)) return null;
  return `${m?.[1] ?? ''}${abs}`;
}

// C-03 人工调账（formDef adjust；入口在用户 360 / 资金流水抽屉）。原因字段标签 06「审核备注（原因）」= fndReasonNote
export function adjustForm(ctx: AdminCtx, user: { id: number; label: string }): FormDef {
  const t = ctx.t;
  const route: RouteKey = 'POST /api/admin/users/{id}/adjust';
  const fields: readonly FieldDef[] = [
    { k: 'amount', label: `${str(t, 'cAmount')} (USDT, +/-)`, type: 'text', placeholder: '-5 / 12.5' },
    { k: 'reason', label: str(t, 'fndReasonNote'), type: 'text', wide: true },
  ];
  return {
    title: `${str(t, 'aAdjust')} · ${user.label}`,
    tip: str(t, 'adjustTip'),
    fields,
    init: { amount: '', reason: '' },
    validate: (v) => {
      if (adjustAmount(String(v['amount'] ?? '')) === null) return str(t, 'errAdjustAmount');
      const reason = String(v['reason'] ?? '').trim();
      return reason.length < 1 || reason.length > NOTE_MAX ? str(t, 'errReason') : '';
    },
    submit: async (c, v) => {
      const amount = adjustAmount(String(v['amount'] ?? ''));
      if (amount === null) throw localErr(route, str(t, 'errAdjustAmount'));
      const alive = sessionGuard(c);
      let r: AdjustResult;
      try {
        r = await c.gp.api.call<AdjustResult>(route, { path: { id: user.id }, body: { amount, reason: String(v['reason']).trim() } });
      } catch (e) {
        // 1101：负向调账超过可用余额（后台错误码表未收录，页内映射）
        if (e instanceof ApiError && e.code === 1101) throw localErr(route, str(t, 'err1101'));
        throw e;
      }
      if (!alive()) return;
      c.gp.ui.toast(str(t, 'toastAdjust').replace('{biz_no}', r.biz_no));
      c.events.emit('profile', {});
    },
  };
}

const PACK = { i18n: I18N_FUNDS, rewrites: REWRITES_FUNDS } as const;

export const PAGES_FUNDS: PageMap = {
  review: {
    page: 'review',
    ...PACK,
    tabLabels: (t) => [...strArr(t, 'tabReview'), str(t, 'tabOverpaid')],
    views: [reviewView('pending'), reviewView('deposit'), reviewView('withdrawal'), reviewView('done'), overpaidView],
  },
};
